import React, { PureComponent } from 'react';
import { throttle } from 'lodash';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ResizeObserver from 'resize-observer-polyfill';
import { actions } from 'src/machete-redux';
import * as viewportUnitsBuggyfill from 'viewport-units-buggyfill';
import utils from 'src/utils';
import constants from 'src/constants';

const { resizeViewer } = utils.machete;

const {
  MACHETE_VIEWER_OPTS_FOR_CREATING_SHARE_SCREENSHOT,
} = constants.jobShare;

const MacheteViewerContainer = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const mapStateToProps = (state) => ({
  initialized: state.machete.viewer.initialized,
});

const mapDispatchToProps = (dispatch) => ({
  init: bindActionCreators(actions.viewer.init, dispatch),
  teardown: bindActionCreators(actions.viewer.teardown, dispatch),
});

export class MacheteViewer extends PureComponent {
  static propTypes = {
    initialized: PropTypes.bool.isRequired,
    init: PropTypes.func.isRequired,
    teardown: PropTypes.func.isRequired,
  };

  componentDidMount() {
    const { initialized, init } = this.props;
    if (!initialized) {
      init({
        container: this.macheteViewer,
        opts: {
          ...MACHETE_VIEWER_OPTS_FOR_CREATING_SHARE_SCREENSHOT,
          useProductsLibraryForTexturing: true,
          environmentType: Hover.Constants.EnvironmentTypes.Default,
        },
      });
    }
    // allows viewport units in mobile safari
    viewportUnitsBuggyfill.init({
      hacks: viewportUnitsBuggyfill.hacks,
    });

    // this delay ensures we resize machete after completing an orientation change
    this.throttledResizeViewer = throttle(resizeViewer, 10);

    this.containerObserver = new ResizeObserver(this.throttledResizeViewer);
    this.containerObserver.observe(this.macheteViewer);
  }

  componentDidUpdate(prevProps) {
    if (this.props.initialized && !prevProps.initialized) {
      /**
       * Select the canvas element within the macheteViewer element
       * and apply the following attribute and style to it: touch-action: "none".
       * This allows pointerMove events to fire for touch devices.
       * For more context see this Jira ticket: https://hoverinc.atlassian.net/browse/NS-601
       */
      const elementsArray = this.macheteViewer.getElementsByTagName('canvas');
      if (elementsArray.length) {
        const canvasElement = elementsArray[0];
        /**
         * Need to set both the HTML attribute and the CSS property because
         * we use the PEP library which polyfills pointer events.
         * The reason we need to set the attribute is because PEP monitors that attribute to
         * see which events need to be added.
         * For more info go here: https://github.com/jquery/PEP#touch-action
         */
        canvasElement.setAttribute('touch-action', 'none');
        canvasElement.style['touch-action'] = 'none';
      }
    }
  }

  componentWillUnmount() {
    this.containerObserver.disconnect();
    this.props.teardown();
  }

  render() {
    return (
      <MacheteViewerContainer
        data-selenium="macheteViewerContainer"
        innerRef={(c) => {
          this.macheteViewer = c;
        }}
      />
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MacheteViewer);
